import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { LoadingController } from "@ionic/angular";
import { ToastComponent } from "src/app/core-files/loaders/toast-messages";
import { AgentCallingService } from "src/app/services/agent-calling.service";

interface CallingMessageItem {
  Id: any;
  Header: string;
  Message: string;
}

@Component({
  selector: "app-whatsapp-message-dialog",
  template: `
    <div class="wa-dialog-shell">
      <div class="wa-scroll-region">
        <div class="wa-dialog-header">
          <div class="wa-header-icon">
            <ion-icon name="logo-whatsapp"></ion-icon>
          </div>
          <div class="wa-header-copy">
            <h2>Select WhatsApp Message</h2>
            <p>Pick a template and send it instantly to +91 {{ data.contactNumber }}</p>
          </div>
          <button mat-icon-button type="button" class="wa-close-btn" (click)="close()" aria-label="Close">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div class="wa-dialog-body">
          <mat-form-field appearance="outline" class="wa-search-field">
            <mat-label>Search template</mat-label>
            <input matInput [(ngModel)]="searchText" (ngModelChange)="applyFilter($event)"
              placeholder="Type header or message" />
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>

          <div class="wa-content-grid">
            <div class="wa-list-panel">
              <div class="wa-list-head">
                <span>Templates</span>
                <span class="wa-count">{{ filteredMessageList.length }}</span>
              </div>

              @if (!isLoading && filteredMessageList.length === 0) {
              <div class="wa-empty-state">
                <mat-icon>forum</mat-icon>
                <p>No message templates found.</p>
              </div>
              }

              <div class="wa-message-list">
                @for (item of filteredMessageList; track item.Id) {
                <button type="button" class="wa-message-card" [class.wa-message-card--active]="selectedMessage?.Id === item.Id"
                  (click)="selectMessage(item)">
                  <div class="wa-card-header">{{ item.Header || 'Untitled' }}</div>
                </button>
                }
              </div>
            </div>

            <div class="wa-preview-panel">
              <div class="wa-preview-head">Message Preview</div>

              @if (selectedMessage) {
              <div class="wa-preview-content">
                <p>{{ selectedMessage.Message }}</p>
              </div>
              } @else {
              <div class="wa-empty-preview">
                <mat-icon>chat</mat-icon>
                <p>Select a template from the left</p>
              </div>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="wa-dialog-actions">
        <button mat-stroked-button type="button" (click)="close()">Cancel</button>
        <button mat-flat-button class="whatsapp-send" type="button" (click)="sendSelectedMessage()" [disabled]="!selectedMessage">
          <mat-icon>send</mat-icon>
          Send WhatsApp
        </button>
      </div>
    </div>
  `,
  styles: [
    `
    .whatsapp-send {
      background: var(--ion-color-secondary-shade) !important;
      color: #fff !important;
    }

    .wa-dialog-shell {
      --wa-primary: #0f8f63;
      --wa-primary-soft: #e9f9f2;
      --wa-border: #d8e7df;
      --wa-text: #14382b;
      --wa-muted: #567468;

      display: flex;
      flex-direction: column;
      gap: 14px;
      min-width: 280px;
      max-height: min(85vh, 760px);
      overflow: hidden;
    }

    .wa-scroll-region {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      display: flex;
      flex-direction: column;
      gap: 14px;
      padding-bottom: 2px;
    }

    .wa-dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
      background: linear-gradient(135deg, #effff8 0%, #f6fbff 100%);
      border: 1px solid var(--wa-border);
      border-radius: 14px;
      padding: 14px;
    }

    .wa-header-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: grid;
      place-items: center;
      background: #d8f7ea;
      color: var(--wa-primary);
      font-size: 24px;
    }

    .wa-header-copy {
      flex: 1;
    }

    .wa-header-copy h2 {
      margin: 0;
      font-size: 15px;
      color: var(--wa-text);
    }

    h2 {
      line-height: normal;
    }

    .wa-header-copy p {
      margin: 4px 0 0;
      font-size: 12px;
      color: var(--wa-muted);
    }

    .wa-close-btn {
      color: #2e4c41;
    }

    .wa-dialog-body {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 0 10px;
    }

    .wa-search-field {
      width: 100%;
    }

    .wa-content-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    .wa-list-panel,
    .wa-preview-panel {
      border: 1px solid var(--wa-border);
      border-radius: 12px;
      background: #fff;
    }

    .wa-list-head,
    .wa-preview-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid #edf4f0;
      color: var(--wa-text);
      font-weight: 500;
    }

    .wa-count {
      background: var(--wa-primary-soft);
      border-radius: 999px;
      padding: 2px 8px;
      font-size: 12px;
      color: var(--wa-primary);
    }

    .wa-message-list {
      max-height: 320px;
      overflow: auto;
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 10px;
    }

    .wa-message-card {
      text-align: left;
      border: 1px solid #dce9e3;
      border-radius: 10px;
      background: #fff;
      padding: 10px;
      cursor: pointer;
      transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    }

    .wa-message-card:hover {
      border-color: #9acfb8;
      transform: translateY(-1px);
      box-shadow: 0 8px 16px rgba(19, 86, 58, 0.08);
    }

    .wa-message-card--active {
      border-color: var(--wa-primary);
      background: #f5fff9;
      box-shadow: 0 0 0 1px var(--ion-color-secondary-shade) inset;
    }

    .wa-card-header {
      font-weight: 600;
      color: #1d4133;
      margin-bottom: 4px;
    }

    .wa-preview-content {
      padding: 12px;
      max-height: 320px;
      overflow: auto;
    }

    .wa-preview-content p {
      margin: 0;
      color: #4e6a5e;
      white-space: pre-wrap;
    }

    .wa-empty-state,
    .wa-empty-preview {
      min-height: 240px;
      display: grid;
      place-content: center;
      text-align: center;
      color: #7a9388;
      gap: 6px;
    }

    .wa-dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      flex: 0 0 auto;
      padding: 0 10px 10px;
      background: #fff;
      z-index: 2;
    }

    @media (max-width: 768px) {
      .wa-content-grid {
        grid-template-columns: 1fr;
      }

      .wa-message-list {
        max-height: 220px;
      }
    }
    `,
  ],
  standalone: false,
})
export class WhatsAppMessageDialogComponent implements OnInit {
  isLoading: boolean = false;
  messageList: CallingMessageItem[] = [];
  filteredMessageList: CallingMessageItem[] = [];
  selectedMessage: CallingMessageItem | null = null;
  searchText: string = "";

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { contactNumber: string; mainAgentId: any },
    private dialogRef: MatDialogRef<WhatsAppMessageDialogComponent>,
    private agentCallingService: AgentCallingService,
    private toastComponent: ToastComponent,
    private loadingController: LoadingController
  ) {}

  ngOnInit(): void {
    this.loadCallingMessages();
  }

  async loadCallingMessages(): Promise<void> {
    this.isLoading = true;
    const loading = await this.loadingController.create({
      cssClass: "my-custom-class",
      message: "Loading message templates...",
    });
    await loading.present();

    this.agentCallingService.GetCallingMessagesList(this.data.mainAgentId).subscribe({
      next: async (res: any) => {
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.Data)
              ? res.Data
              : [];

        this.messageList = list.map((item: any) => ({
          Id: item?.Id,
          Header: item?.Header || "Untitled",
          Message: item?.Message || "",
        }));
        this.filteredMessageList = [...this.messageList];
        this.selectedMessage = this.filteredMessageList[0] || null;
        this.isLoading = false;
        await loading.dismiss();
      },
      error: async () => {
        this.isLoading = false;
        await loading.dismiss();
        this.toastComponent.showToast("Unable to load calling messages", "danger");
      },
    });
  }

  applyFilter(value: string): void {
    const q = value.trim().toLowerCase();
    if (!q) {
      this.filteredMessageList = [...this.messageList];
      return;
    }

    this.filteredMessageList = this.messageList.filter((item: CallingMessageItem) =>
      item.Header.toLowerCase().includes(q) || item.Message.toLowerCase().includes(q)
    );
  }

  selectMessage(item: CallingMessageItem): void {
    this.selectedMessage = item;
  }

  sendSelectedMessage(): void {
    if (!this.selectedMessage) {
      this.toastComponent.showToast("Please select a message to send", "warning");
      return;
    }

    const messageText = [this.selectedMessage.Header, this.selectedMessage.Message]
      .filter((part: string) => !!part)
      .join("\n\n");

    const url =
      "whatsapp://send?phone=+91" +
      this.data.contactNumber +
      "&text=" +
      encodeURIComponent(messageText);
    window.open(url, "_system");
    this.dialogRef.close(true);
  }

  close(): void {
    this.dialogRef.close(false);
  }
}